import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  BookOpen, Calendar, Clock, ArrowRight, Tag, 
  Plane, Ship, Truck, Globe, TrendingUp, Leaf
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const BlogPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';

  // Sample blog posts - In production, these would come from a CMS or API
  const featuredPost = {
    id: 1,
    title: isSpanish 
      ? 'El Futuro del Transporte de Carga: Tendencias 2026' 
      : 'The Future of Cargo Transport: 2026 Trends',
    excerpt: isSpanish
      ? 'Exploramos las principales tendencias que están transformando la industria logística, desde la automatización hasta la sostenibilidad.'
      : 'We explore the key trends transforming the logistics industry, from automation to sustainability.',
    image: '/assets/banner-ocean-freight.jpg',
    category: isSpanish ? 'Industria' : 'Industry',
    date: '2026-03-15',
    readTime: '8 min'
  };

  const posts = [
    {
      id: 2,
      title: isSpanish 
        ? 'Guía Completa: Incoterms 2020' 
        : 'Complete Guide: Incoterms 2020',
      excerpt: isSpanish
        ? 'Todo lo que necesita saber sobre los términos de comercio internacional y cómo afectan sus envíos.'
        : 'Everything you need to know about international trade terms and how they affect your shipments.',
      image: '/assets/banner-documentation.jpg',
      category: isSpanish ? 'Educación' : 'Education',
      icon: BookOpen,
      date: '2026-03-10',
      readTime: '12 min'
    },
    {
      id: 3,
      title: isSpanish 
        ? 'Transporte Aéreo vs Marítimo: ¿Cuál Elegir?' 
        : 'Air vs Ocean Freight: Which to Choose?',
      excerpt: isSpanish
        ? 'Analizamos las ventajas y desventajas de cada modo de transporte para ayudarle a tomar la mejor decisión.'
        : 'We analyze the advantages and disadvantages of each transport mode to help you make the best decision.',
      image: '/assets/banner-air-cargo.jpg',
      category: isSpanish ? 'Servicios' : 'Services',
      icon: Plane,
      date: '2026-03-05',
      readTime: '6 min'
    },
    {
      id: 4,
      title: isSpanish 
        ? 'Sostenibilidad en la Cadena de Suministro' 
        : 'Sustainability in the Supply Chain',
      excerpt: isSpanish
        ? 'Cómo las empresas están reduciendo su huella de carbono en operaciones logísticas.'
        : 'How companies are reducing their carbon footprint in logistics operations.',
      image: '/assets/sector-oil-energy.jpg',
      category: isSpanish ? 'Sostenibilidad' : 'Sustainability',
      icon: Leaf,
      date: '2026-02-28',
      readTime: '5 min'
    },
    {
      id: 5,
      title: isSpanish 
        ? 'Optimización de Rutas de Transporte Terrestre' 
        : 'Optimizing Road Transport Routes',
      excerpt: isSpanish
        ? 'Estrategias para mejorar la eficiencia y reducir costos en el transporte por carretera.'
        : 'Strategies to improve efficiency and reduce costs in road transportation.',
      image: '/assets/banner-trucking.jpg',
      category: isSpanish ? 'Operaciones' : 'Operations',
      icon: Truck,
      date: '2026-02-20',
      readTime: '7 min'
    },
    {
      id: 6,
      title: isSpanish 
        ? 'Comercio Internacional: Oportunidades 2026' 
        : 'International Trade: 2026 Opportunities',
      excerpt: isSpanish
        ? 'Los mercados emergentes y las nuevas rutas comerciales que están redefiniendo el comercio global.'
        : 'Emerging markets and new trade routes that are redefining global commerce.',
      image: '/assets/sector-industrial.jpg',
      category: isSpanish ? 'Mercados' : 'Markets',
      icon: Globe,
      date: '2026-02-15',
      readTime: '9 min'
    }
  ];

  const categories = [
    { name: isSpanish ? 'Todos' : 'All', count: 6 },
    { name: isSpanish ? 'Industria' : 'Industry', count: 2 },
    { name: isSpanish ? 'Educación' : 'Education', count: 1 },
    { name: isSpanish ? 'Servicios' : 'Services', count: 1 },
    { name: isSpanish ? 'Sostenibilidad' : 'Sustainability', count: 1 },
    { name: isSpanish ? 'Operaciones' : 'Operations', count: 1 }
  ];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(isSpanish ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-steel-50" data-testid="blog-page">
      {/* Hero Section */}
      <section className="bg-navy-900 pt-32 pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full text-gold text-sm font-medium tracking-wider uppercase mb-6">
              {isSpanish ? 'Blog & Noticias' : 'Blog & News'}
            </span>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
              {isSpanish ? 'Insights de Logística' : 'Logistics Insights'}
            </h1>
            <p className="text-steel-300 text-lg">
              {isSpanish 
                ? 'Artículos, guías y noticias sobre la industria del transporte y la logística internacional.'
                : 'Articles, guides, and news about the transport industry and international logistics.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="relative rounded-xl overflow-hidden shadow-2xl group cursor-pointer">
              <div className="aspect-[21/9] relative">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-gold text-navy-900 text-sm font-bold rounded-full">
                    {isSpanish ? 'Destacado' : 'Featured'}
                  </span>
                  <span className="text-gold text-sm flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    {featuredPost.category}
                  </span>
                </div>
                <h2 className="font-outfit text-2xl md:text-4xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                  {featuredPost.title}
                </h2>
                <p className="text-steel-300 text-lg mb-6 max-w-2xl">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center gap-6 text-steel-400 text-sm">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {formatDate(featuredPost.date)}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {featuredPost.readTime} {isSpanish ? 'de lectura' : 'read'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 bg-steel-50">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Posts Grid */}
            <div className="flex-1">
              <div className="grid md:grid-cols-2 gap-8">
                {posts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                  >
                    <Card className="h-full overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 to-transparent" />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-navy-800 text-xs font-semibold rounded-full flex items-center gap-1">
                            <post.icon className="w-3 h-3" />
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <h3 className="font-outfit text-lg font-bold text-navy-800 mb-3 group-hover:text-gold transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-steel-600 text-sm mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-steel-500 text-xs">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Load More */}
              <div className="text-center mt-12">
                <Button variant="outline" className="border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-white">
                  {isSpanish ? 'Cargar Más Artículos' : 'Load More Articles'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:w-80 space-y-8">
              {/* Categories */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-lg shadow-lg p-6"
              >
                <h3 className="font-outfit font-bold text-navy-800 mb-4">
                  {isSpanish ? 'Categorías' : 'Categories'}
                </h3>
                <ul className="space-y-3">
                  {categories.map((cat) => (
                    <li key={cat.name}>
                      <button className="w-full flex items-center justify-between text-steel-600 hover:text-gold transition-colors">
                        <span>{cat.name}</span>
                        <span className="text-xs bg-steel-100 px-2 py-1 rounded-full">{cat.count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Newsletter */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-navy-800 rounded-lg shadow-lg p-6 text-center"
              >
                <TrendingUp className="w-10 h-10 text-gold mx-auto mb-4" />
                <h3 className="font-outfit font-bold text-white mb-2">
                  {isSpanish ? 'Suscríbete' : 'Subscribe'}
                </h3>
                <p className="text-steel-400 text-sm mb-4">
                  {isSpanish 
                    ? 'Recibe las últimas noticias de logística en tu email.'
                    : 'Get the latest logistics news in your inbox.'}
                </p>
                <Button className="w-full bg-gold text-navy-800 hover:bg-gold/90">
                  {isSpanish ? 'Suscribirse' : 'Subscribe Now'}
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
